import { useI18N } from '@shared-core/hooks/use-i18n'
import { AppIcon } from '@shared-ui/components/ui/app-icon'
import { Column, Row } from '@shared-ui/components/ui/grid'
import { GText } from '@shared-ui/components/ui/text'
import { useAppTheme } from '@shared-ui/theme'
import { rh } from '@shared-ui/theme/scale'
import { useEffect } from 'react'
import { Pressable } from 'react-native'
import Reanimated, {
  cancelAnimation,
  Easing,
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withTiming,
} from 'react-native-reanimated'

import locales from './index.i18n.json'
import { useCreateStyles } from './styles'

type CardActionsProps = {
  onBlock: () => void
  onFreeze: () => void
  onPin: () => void
  isFrozen: boolean
  isLoading: boolean
}

type CardFreezeActionIconProps = { isFrozen: boolean; isLoading: boolean }

function CardFreezeActionIcon({ isFrozen, isLoading }: CardFreezeActionIconProps) {
  const theme = useAppTheme()
  const rotation = useSharedValue(0)
  const swayDistance = rh(1.5)
  useEffect(() => {
    if (isLoading) {
      rotation.value = withRepeat(withTiming(360, { duration: 1200, easing: Easing.linear }), -1, false)
    } else {
      cancelAnimation(rotation)
      rotation.value = 0
    }
    return () => cancelAnimation(rotation)
  }, [isLoading, rotation])
  const animatedStyle = useAnimatedStyle(() => {
    if (!isFrozen) return { transform: [{ rotate: `${rotation.value}deg` }] }
    const phase = (rotation.value * Math.PI) / 180
    return {
      transform: [
        { rotate: `${Math.sin(phase) * 7}deg` },
        { translateY: Math.sin(phase * 2) * swayDistance },
        { scaleY: 1 + Math.sin(phase) * 0.05 },
      ],
    }
  })
  return (
    <Reanimated.View style={animatedStyle}>
      <AppIcon
        icon={isFrozen ? 'unfreeze' : 'freeze'}
        size={24}
        color={isFrozen ? theme.COLOR_TEXT_INVERSE_PRIMARY : undefined}
      />
    </Reanimated.View>
  )
}

export function CardActions({ onBlock, onFreeze, onPin, isFrozen, isLoading }: CardActionsProps) {
  const styles = useCreateStyles()
  const { t } = useI18N(locales)
  const handlers = { lock: onBlock, freeze: onFreeze, pin: onPin }

  return (
    <Row justify="center" gap={64} style={styles.cardActions}>
      {(['lock', 'freeze', 'pin'] as const).map((action) => (
        <Pressable
          key={action}
          accessibilityRole="button"
          accessibilityLabel={t(action === 'freeze' && isFrozen ? 'unfreeze' : action)}
          disabled={isLoading}
          style={styles.actionItem}
          onPress={handlers[action]}
        >
          <Column align="center" gap={8}>
            <Column
              center
              style={[
                styles.actionCircle,
                action === 'lock' && styles.criticalCircle,
                action === 'freeze' && isFrozen && styles.frozenCircle,
              ]}
            >
              {action === 'freeze' && <CardFreezeActionIcon isFrozen={isFrozen} isLoading={isLoading} />}
              {action !== 'freeze' && <AppIcon icon={action} size={24} isDanger={action === 'lock'} />}
            </Column>
            <GText size={13} style={styles.actionLabel}>
              {t(action === 'freeze' && isFrozen ? 'unfreeze' : action)}
            </GText>
          </Column>
        </Pressable>
      ))}
    </Row>
  )
}
